/** @format */

import { ReactNode, useState } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MathText } from "./MathText";

interface MathTextFieldProps {
	/** Id linking the label to the textarea. */
	id: string;
	/** Field label. */
	label: ReactNode;
	/** Current field value, used to render the KaTeX preview. */
	value: string;
	/** `react-hook-form` registration to spread onto the textarea. */
	registration: UseFormRegisterReturn;
	/** Base styling shared by the textarea and preview surface. */
	textareaClassName: string;
	rows?: number;
}

/**
 * A content-block text field with a Preview/Edit toggle. In edit mode it is a
 * plain textarea (authors type LaTeX inline as `$...$` / `$$...$$`); in preview
 * mode the current value is typeset with KaTeX so the author can check the
 * maths, then switch straight back to editing.
 */
export const MathTextField = ({ id, label, value, registration, textareaClassName, rows = 5 }: MathTextFieldProps) => {
	const { t } = useTranslation();
	const [isPreview, setIsPreview] = useState(false);
	const hasContent = Boolean(value?.trim());

	return (
		<div className="flex flex-col gap-1">
			<div className="flex items-center justify-between gap-2">
				<label htmlFor={id} className="font-semibold">
					{label}
				</label>
				<button
					type="button"
					aria-pressed={isPreview}
					onClick={() => setIsPreview((isPrevious) => !isPrevious)}
					className="rounded-md border border-gray-300 px-2 py-0.5 text-xs font-semibold"
				>
					{isPreview ? t("questionForm.mathEdit") : t("questionForm.mathPreview")}
				</button>
			</div>
			{isPreview ? (
				<div
					data-testid={`${id}-preview`}
					className={`${textareaClassName} w-full min-h-34 overflow-auto whitespace-pre-wrap`}
				>
					{hasContent ? (
						<MathText text={value} />
					) : (
						<span className="text-gray-400">{t("questionForm.mathPreviewEmpty")}</span>
					)}
				</div>
			) : (
				<textarea id={id} rows={rows} className={`${textareaClassName} w-full text-m resize-none`} {...registration} />
			)}
		</div>
	);
};
